import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Flame } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PIZZA_IMAGE = "https://customer-assets.emergentagent.com/job_f3844245-0dd8-49b3-8183-1c9d7505a1eb/artifacts/14srfmdg_22.png";

const Hero = () => {
  const [reviewData, setReviewData] = useState({ rating: 4.8, reviewCount: 443, googleLink: '' });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reviews`);
        setReviewData(res.data);
      } catch (e) {}
    };
    fetchReviews();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center bg-black overflow-hidden" data-testid="hero-section">
      {/* Background four à bois avec opacité basse */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.15]"
        style={{ backgroundImage: 'url("https://images.pexels.com/photos/29626982/pexels-photo-29626982.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")' }}
      />
      {/* Overlay sombre pour assurer lisibilité */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/60" />
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        }}
      />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-900/10 rounded-full blur-3xl" />

      {/* Container max 1280px */}
      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-16 pt-32 pb-16 md:pt-36 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center overflow-visible">

          {/* LEFT - Texte */}
          <div className="text-center md:text-left order-1">
            {/* Titre LA CASA DEL PIZZA */}
            <h2
              className="text-white font-black text-4xl sm:text-5xl lg:text-6xl uppercase leading-none tracking-wide mb-1 whitespace-nowrap"
              style={{ fontFamily: "'Bebas Neue', 'Arial Black', sans-serif" }}
            >
              La Casa <span className="text-red-600">Del</span> Pizza
            </h2>
            <p
              className="text-white/80 font-bold text-sm sm:text-lg uppercase tracking-[0.25em] mb-5 sm:mb-6"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Au feu de bois
            </p>

            {/* Accroche */}
            <h1
              className="text-xl sm:text-2xl lg:text-3xl leading-tight mb-2 sm:mb-3 text-white font-black uppercase"
              style={{ fontFamily: "'Bebas Neue', 'Arial Black', sans-serif", letterSpacing: '0.04em' }}
            >
              La vraie pizza au feu de bois,
              <br />
              ici à Marseille 🍕🔥
            </h1>

            {/* Sous-titre */}
            <p
              className="text-white font-bold text-sm sm:text-base md:text-lg mb-3 sm:mb-5"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Généreuse, croustillante, faite avec passion
            </p>

            {/* Points forts */}
            <div className="space-y-0.5 sm:space-y-1 mb-5 sm:mb-8">
              <p className="text-gray-300 font-semibold text-xs sm:text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Déjà adoptée par les Marseillais
              </p>
              <p className="text-gray-300 font-semibold text-xs sm:text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                🚀 Livraison rapide, toujours chaude
              </p>
              <p className="text-gray-300 font-semibold text-xs sm:text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                👉 Goûte-la… tu vas comprendre
              </p>
            </div>

            {/* Boutons */}
            <div className="flex flex-col sm:flex-row items-center md:items-start gap-3 sm:gap-4 mb-5 sm:mb-8">
              <Link to="/menu" onClick={() => { if (window.gtag) window.gtag('event', 'order_click'); }}>
                <div
                  className="bg-red-600 hover:bg-red-700 text-white font-bold text-sm sm:text-base px-8 sm:px-10 py-4 rounded-xl shadow-2xl shadow-red-600/30 hover:scale-105 transition-all duration-300 flex items-center gap-2.5 cursor-pointer whitespace-nowrap"
                  data-testid="hero-cta-menu"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  <span className="text-lg">🍕</span>
                  Je commande
                </div>
              </Link>
              <a href="tel:0767682464">
                <div
                  className="bg-transparent border-2 border-gray-600 hover:border-white text-white font-bold text-sm sm:text-base px-8 sm:px-10 py-4 rounded-xl hover:scale-105 transition-all duration-300 flex items-center gap-2.5 cursor-pointer whitespace-nowrap"
                  data-testid="hero-cta-appeler"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  <Phone size={18} />
                  Appeler
                </div>
              </a>
            </div>

            {/* Google badge + Adresse */}
            <div className="flex flex-col sm:flex-row items-center md:items-start gap-3 sm:gap-6">
              <a
                href={reviewData.googleLink || "https://www.google.com/search?q=La+Casa+Del+Pizza+Feu+de+bois+Marseille+avis"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-3 py-1 hover:bg-yellow-500/20 hover:scale-105 transition-all duration-300 cursor-pointer"
                data-testid="google-reviews-badge"
              >
                <span className="text-xs">&#11088;</span>
                <span className="text-yellow-300 font-semibold text-xs">{reviewData.rating}/5</span>
                <span className="text-yellow-200/50 text-xs">({reviewData.reviewCount} avis)</span>
              </a>
              <div className="flex items-center gap-1.5 text-gray-500 text-xs" data-testid="hero-address">
                <MapPin size={12} className="text-red-500/50" />
                <span>20 Bd Baudelaire, 13005 Marseille</span>
              </div>
            </div>
          </div>

          {/* RIGHT - Pizza (cachée sur mobile) */}
          <div className="order-2 hidden md:flex justify-center">
            <div className="relative w-[600px] lg:w-[700px] xl:w-[780px] md:absolute md:right-[5%] md:top-1/2 md:-translate-y-1/2">
              <div className="absolute inset-0 bg-red-600/8 rounded-full blur-3xl scale-110" />
              <img
                src={PIZZA_IMAGE}
                alt="Pizza au feu de bois"
                className="relative z-10 w-full h-auto drop-shadow-2xl"
                data-testid="hero-logo"
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
